import * as React from 'react'
import styled from 'styled-components'
import { Button } from '../frontend-lib/components/common/Button'
import Container from '../frontend-lib/components/common/Container'
import { Input } from '../frontend-lib/components/common/Input'
import Section from '../frontend-lib/components/common/Section'
import { Select } from '../frontend-lib/components/common/Select'
import StylistList from '../frontend-lib/components/common/StylistList'
import { useExploreState } from '../frontend-lib/components/explore/useExploreState'
import { loadCatalogue } from '../frontend-lib/i18n'
import { I18nCatalogue } from '../frontend-lib/i18n/explore/types'
import { routeFactories } from '../frontend-lib/routes'
import { colors, devices } from '../frontend-lib/styleConstants'
import { Areas, fetchAreas } from '../frontend-lib/types/area'
import {
  fetchSearchHairStylists,
  fetchTopSearchHairStylists,
  HairStylist,
  SearchItems,
} from '../frontend-lib/types/hairStylist'
import { KamismaPageContext } from './_app'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly areas: Areas
  readonly stylists: HairStylist[]
}

const Explore = ({ catalogue, areas, stylists }: Props) => {
  const exploreState = useExploreState(stylists)

  const transformAreaName = (id: number, name: string): string => {
    if (id === 1) {
      // 英語表記（アルファベットを含む場合）
      if (/[\w]/.test(name)) {
        return `Tokyo - ${name}`;
      }
      // 日本語表記（漢字を含む場合）
      if (/[\u4E00-\u9FFF]/.test(name)) {
        return `東京都 - ${name}`;
      }
    }
    // 上記以外
    if ([2, 3, 4, 5, 6, 9, 12, 19].includes(id)) {
      return `　　　 - ${name}`;
    }
    return name;
  }

  const options = [
    { value: 0, label: catalogue.selectArea },
    ...areas.map(d => {
      return {
        value: d.id,
        label: transformAreaName(d.id, d.name),
      }
    }),
  ]

  const onAreaSelectChange = React.useCallback<(e: React.ChangeEvent<HTMLSelectElement>) => void>(
    e => exploreState.setState({ ...exploreState, areaId: e.target.value }),
    [exploreState],
  )

  const onStarSelectChange = React.useCallback<(e: React.ChangeEvent<HTMLSelectElement>) => void>(
    e => exploreState.setState({ ...exploreState, selectedStarValue: Number(e.target.value) }),
    [exploreState],
  )

  const onNameChange = React.useCallback<(e: React.ChangeEvent<HTMLInputElement>) => void>(
    e => exploreState.setState({ ...exploreState, name: e.target.value }),
    [exploreState],
  )

  const onFreeWordChange = React.useCallback<(e: React.ChangeEvent<HTMLInputElement>) => void>(
    e => exploreState.setState({ ...exploreState, freeWord: e.target.value }),
    [exploreState],
  )

  const onExploreButtonClick = React.useCallback(async () => {
    let searchItems: SearchItems = {}

    if (exploreState.name !== '') {
      searchItems.stylistName = exploreState.name
    }

    if (exploreState.areaId !== '0') {
      searchItems.areaId = exploreState.areaId
    }

    if (exploreState.selectedStarValue !== 0) {
      searchItems.starOption = exploreState.selectedStarValue === 1 ? 'true' : 'false'
    }

    if (exploreState.freeWord !== '') {
      searchItems.freeWord = exploreState.freeWord
    }

    history.pushState(null, '', createSearchURL(searchItems))

    const searchResultLists = await fetchSearchHairStylists(searchItems)

    exploreState.setState({
      ...exploreState,
      stylists: searchResultLists ? searchResultLists : [],
    })
  }, [exploreState])

  return (
    <>
      <Container>
        <Title>{catalogue.title}</Title>
      </Container>
      <SearchContainer data-test="search-container">
        <Container>
          <div>
            <SubTitle>{catalogue.titleName}</SubTitle>
            <Input
              value={exploreState.name}
              type="search"
              name="input-search-name"
              style={{ width: '100%', height: '40px' }}
              onChange={onNameChange}
            />
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <SubTitle>{catalogue.titleArea}</SubTitle>
            <Select
              name="select-area-option"
              fluid
              style={{ height: '40px' }}
              options={options}
              onChange={onAreaSelectChange}
            />
          </div>

          <div style={{ marginTop: '2rem' }}>
            <SubTitle>{catalogue.titleStar}</SubTitle>
            <Select
              name="select-star-option"
              fluid
              style={{ height: '40px' }}
              options={catalogue.starSelectData}
              onChange={onStarSelectChange}
            />
          </div>

          <div>
            <SubTitle>{catalogue.freeWord}</SubTitle>
            <Input
              value={exploreState.freeWord}
              type="search"
              name="input-search-free-word"
              style={{ width: '100%', height: '40px' }}
              onChange={onFreeWordChange}
            />
          </div>

          <div style={{ textAlign: 'center' }}>
            <Button look="primary" bordered style={{ marginTop: '1.5rem' }} onClick={onExploreButtonClick}>
              {catalogue.searchButtonText}
            </Button>
          </div>
        </Container>
      </SearchContainer>

      <Section bgColor="white">
        <Container data-test="stylist-container">
          <StylistList stylists={exploreState.stylists} catalogue={catalogue} />
        </Container>
      </Section>
    </>
  )
}

Explore.getInitialProps = async ({ req, me }: KamismaPageContext) => {
  const catalogue = await loadCatalogue<I18nCatalogue>('explore', me, req)
  const areas = await fetchAreas(req)
  const stylists = await fetchTopSearchHairStylists(req)

  return { catalogue, areas, stylists }
}

export default Explore

const Title = styled.h1`
  margin: 0;
  padding: 40px 0;
  font-size: 36px;
  font-weight: bold;

  @media ${devices.mobile} {
    font-size: 28px;
  }
`

const SubTitle = styled.h3`
  margin: 0 0 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${colors.secondaryDark};
  font-size: 24px;
  line-height: 36px;
`
const SearchContainer = styled.div`
  background-color: ${colors.secondaryExtraLight};
  padding: 40px 0;
`

function createSearchURL(searchItems: SearchItems) {
  let url = routeFactories.explore.pathname
  let paramAddedFlug = false

  if (searchItems.stylistName != null) {
    url += '?name=' + searchItems.stylistName
    paramAddedFlug = true
  }

  if (searchItems.areaId != null) {
    url += paramAddedFlug ? '&' : '?'
    url += 'area_id=' + searchItems.areaId
    paramAddedFlug = true
  }

  if (searchItems.starOption != null) {
    url += paramAddedFlug ? '&' : '?'
    url += 'has_star=' + searchItems.starOption
  }

  if (searchItems.freeWord != null) {
    url += '?free_word=' + searchItems.freeWord
    paramAddedFlug = true
  }

  return url
}
